import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LandingPage } from './components/LandingPage';
import { AuthPage } from './components/AuthPage';
import { Dashboard } from './components/Dashboard';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { VerifyEmailPage } from './components/auth/VerifyEmailPage';
import { Toaster } from 'sonner';
import { fetchCurrentUser, saveUser, clearAuthData, logout as apiLogout } from './utils/auth';
import { Footer } from './components/Footer';
import { CookieConsent } from './components/CookieConsent';
import { Seo } from './components/seo/Seo';
import DocPage from './pages/DocPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FaqPage';
import GuidesPage from './pages/GuidesPage';
import GuideArticlePage from './pages/GuideArticlePage';
import NotFoundPage from './pages/NotFoundPage';
import { privacyDoc } from './content/privacy';
import { termsDoc } from './content/terms';
import { cookiesDoc, guidelinesDoc } from './content/policies';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Global axios config: always send httpOnly auth cookie
axios.defaults.withCredentials = true;

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const initializeAuth = useCallback(async () => {
    // Source of truth is the server: cookie -> /users/me
    const currentUser = await fetchCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Set when the user deliberately signs out/deletes, so the guarded /dashboard route sends them home, not to /auth.
  const signedOutRef = useRef(false);

  const handleAuthSuccess = useCallback((userData) => {
    signedOutRef.current = false;
    saveUser(userData);
    setUser(userData);
    navigate('/dashboard');
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await apiLogout();
    signedOutRef.current = true;
    setUser(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleAccountDeleted = useCallback(() => {
    clearAuthData();
    signedOutRef.current = true;
    setUser(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleGetStarted = useCallback(() => {
    if (user) navigate('/dashboard');
    else navigate('/auth');
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0047FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#71717A] text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<><Seo /><LandingPage onGetStarted={handleGetStarted} /></>} />
        <Route
          path="/auth"
          element={
            user ? <Navigate to="/dashboard" replace /> : <><Seo title="Sign in or create an account" noindex /><AuthPage onAuthSuccess={handleAuthSuccess} /></>
          }
        />
        <Route path="/reset-password" element={<><Seo title="Reset password" noindex /><ResetPasswordPage /></>} />
        <Route path="/verify-email" element={<><Seo title="Verify email" noindex /><VerifyEmailPage /></>} />
        <Route
          path="/dashboard"
          element={
            user ? <><Seo title="Dashboard" noindex /><Dashboard user={user} onLogout={handleLogout} onAccountDeleted={handleAccountDeleted} /></> : <Navigate to={signedOutRef.current ? '/' : '/auth'} replace />
          }
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/guides/:slug" element={<GuideArticlePage />} />
        <Route path="/privacy" element={<DocPage doc={privacyDoc} testId="privacy-page" />} />
        <Route path="/terms" element={<DocPage doc={termsDoc} testId="terms-page" />} />
        <Route path="/cookies" element={<DocPage doc={cookiesDoc} testId="cookies-page" />} />
        <Route path="/community-guidelines" element={<DocPage doc={guidelinesDoc} testId="guidelines-page" />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" expand={true} richColors />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
